// src/components/navigation/AlarmNavigationPanel.jsx
// Navigation panel for alarm details - simplified without GPS tracking
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Navigation as NavigationIcon,
  DirectionsCar as CarIcon,
  DirectionsWalk as WalkIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DoorFront as EntranceIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  LocationOff as LocationOffIcon,
  Map as MapIcon,
  GpsOff as GpsOffIcon,
  Route as RouteIcon,
} from '@mui/icons-material';
import { NavigationProvider, useNavigation } from '../../contexts/NavigationContext';
import NavigationContainer from './NavigationContainer';
import indoorMappingService from '../../services/indoorMappingService';

/**
 * Inner component that uses navigation context
 */
const AlarmNavigationPanelInner = ({
  alarmLocation,
  destinationCoords: propDestinationCoords,
  floorPlanId,
  wayfindingNodeId,
  onNavigationStart,
  isLoading: externalLoading = false,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [showIndoorNav, setShowIndoorNav] = useState(false);
  const [entrances, setEntrances] = useState([]);
  const [selectedEntrance, setSelectedEntrance] = useState(null);
  const [loadingEntrances, setLoadingEntrances] = useState(false);
  const [destinationCoords, setDestinationCoords] = useState(propDestinationCoords);
  const [loadingDestination, setLoadingDestination] = useState(false);

  const {
    startNavigationToCustom,
    startNavigationToNode,
    isLoading: navLoading,
    error: navError,
  } = useNavigation();

  // Fetch destination coordinates from wayfinding node if provided
  useEffect(() => {
    const fetchNodeCoords = async () => {
      if (propDestinationCoords) {
        setDestinationCoords(propDestinationCoords);
        return;
      }

      if (!wayfindingNodeId) return;

      setLoadingDestination(true);
      try {
        const node = await indoorMappingService.getWayfindingNode(wayfindingNodeId);
        if (node?.geo_lat && node?.geo_lng) {
          setDestinationCoords({
            lat: parseFloat(node.geo_lat),
            lng: parseFloat(node.geo_lng),
          });
        }
      } catch (err) {
        console.warn('Could not fetch node coordinates:', err.message);
      } finally {
        setLoadingDestination(false);
      }
    };

    fetchNodeCoords();
  }, [wayfindingNodeId, propDestinationCoords]);

  // Load building entrances
  useEffect(() => {
    const loadEntrances = async () => {
      if (!alarmLocation?.id) return;

      // Get location ID for entrances (might need parent location)
      const locationId = alarmLocation.parentLocation || alarmLocation.id;

      setLoadingEntrances(true);
      try {
        const data = await indoorMappingService.getEntrances(locationId);
        setEntrances(data || []);
        if (data?.length > 0) {
          setSelectedEntrance(data[0]);
        }
      } catch (err) {
        console.warn('Could not load entrances:', err.message);
        setEntrances([]);
      } finally {
        setLoadingEntrances(false);
      }
    };

    loadEntrances();
  }, [alarmLocation]);

  // Open Google Maps for outdoor navigation
  const handleOutdoorNavigation = useCallback(() => {
    if (!destinationCoords) return;

    const { lat, lng } = destinationCoords;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    window.open(url, '_blank');
  }, [destinationCoords]);

  // Start indoor navigation - calculates route using backend A* pathfinding
  const handleIndoorNavigation = useCallback(async () => {
    if (!floorPlanId) {
      console.warn('Missing floor plan for indoor navigation');
      return;
    }

    if (!selectedEntrance?.id) {
      console.warn('No entrance selected for indoor navigation - entrance is required');
      return;
    }

    try {
      console.log('[AlarmNavigationPanel] Starting indoor navigation:', {
        floorPlanId,
        entranceId: selectedEntrance.id,
        destination: destinationCoords,
        wayfindingNodeId,
      });

      let routeData;

      // If we have a wayfinding node ID, use node-to-node navigation
      if (wayfindingNodeId) {
        console.log('[AlarmNavigationPanel] Using wayfinding node ID for destination:', wayfindingNodeId);

        // Build destination info from alarm location for display on arrival screen
        const destinationInfo = alarmLocation ? {
          name: alarmLocation.friendlyName || alarmLocation.name || 'Alarm Location',
          description: alarmLocation.description || null,
          floor_plan_id: floorPlanId,
          metadata: alarmLocation.metadata || {},
          coordinates: destinationCoords
        } : null;

        routeData = await startNavigationToNode(
          selectedEntrance.id,
          wayfindingNodeId,
          {
            accessible_only: false,
            prefer_elevators: false,
            use_cache: true,
            destinationInfo
          }
        );
      } else if (destinationCoords) {
        // Fallback: use custom coordinates (less accurate)
        console.log('[AlarmNavigationPanel] Using geo coordinates for destination (fallback)');
        routeData = await startNavigationToCustom(
          floorPlanId,
          destinationCoords.lng,
          destinationCoords.lat,
          selectedEntrance.id
        );
      } else {
        console.warn('No wayfinding node or coordinates available for navigation');
        return;
      }

      console.log('[AlarmNavigationPanel] Route calculated:', {
        routeId: routeData?.route?.id,
        totalSteps: routeData?.steps?.length,
        totalDistance: routeData?.route?.total_distance_meters,
        totalTime: routeData?.route?.total_time_seconds,
      });

      setShowIndoorNav(true);
      if (onNavigationStart) {
        onNavigationStart(routeData);
      }
    } catch (err) {
      console.error('Failed to start indoor navigation:', err);
    }
  }, [floorPlanId, destinationCoords, wayfindingNodeId, selectedEntrance, alarmLocation, startNavigationToNode, startNavigationToCustom, onNavigationStart]);

  // Collect missing data for diagnostics
  const getMissingData = () => {
    const missing = [];

    if (!alarmLocation) {
      missing.push({
        icon: <LocationOffIcon fontSize="small" />,
        message: 'Alarm has no associated location',
        severity: 'error'
      });
    }

    if (!wayfindingNodeId && alarmLocation) {
      missing.push({
        icon: <RouteIcon fontSize="small" />,
        message: 'Location not linked to indoor navigation node',
        severity: 'warning'
      });
    }

    if (!floorPlanId && alarmLocation) {
      missing.push({
        icon: <MapIcon fontSize="small" />,
        message: 'No floor plan available for this location',
        severity: 'warning'
      });
    }

    if (!destinationCoords && !wayfindingNodeId) {
      missing.push({
        icon: <GpsOffIcon fontSize="small" />,
        message: 'Location has no GPS coordinates for outdoor navigation',
        severity: 'info'
      });
    }

    return missing;
  };

  const missingData = getMissingData();
  const hasNavigationCapability = destinationCoords || floorPlanId;

  // Don't render if no destination AND no alarm location AND not loading
  if (!destinationCoords && !alarmLocation && !externalLoading && !loadingDestination) {
    return null;
  }

  const isAnyLoading = externalLoading || loadingDestination || loadingEntrances;

  return (
    <>
      <Paper elevation={2} sx={{ mb: 2 }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NavigationIcon color="primary" />
            <Typography variant="h6">Navigation</Typography>
            {isAnyLoading && (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            )}
          </Box>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Divider />
          <Box sx={{ p: 2 }}>
            {/* Loading State */}
            {isAnyLoading && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Loading navigation data...
              </Alert>
            )}

            {/* Missing Data Indicators */}
            {!isAnyLoading && missingData.length > 0 && !hasNavigationCapability && (
              <Alert severity="warning" icon={<InfoIcon />} sx={{ mb: 2 }}>
                <AlertTitle>Navigation Limited</AlertTitle>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Some navigation features are unavailable:
                </Typography>
                <List dense disablePadding>
                  {missingData.map((item, index) => (
                    <ListItem key={index} disablePadding sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.message}
                        primaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Outdoor Navigation - Always available */}
              <Button
                variant="outlined"
                startIcon={<CarIcon />}
                onClick={handleOutdoorNavigation}
                disabled={!destinationCoords}
                fullWidth
              >
                Get Directions (Google Maps)
              </Button>

              {/* Indoor Navigation - Available when floor plan and entrance exist */}
              {floorPlanId && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<WalkIcon />}
                  onClick={handleIndoorNavigation}
                  disabled={navLoading || !floorPlanId || !selectedEntrance}
                  fullWidth
                >
                  {navLoading ? 'Calculating route...' :
                   loadingEntrances ? 'Loading entrances...' :
                   !selectedEntrance ? 'No entrance available' :
                   'Start Indoor Navigation'}
                </Button>
              )}

              {/* Show message if no entrances found */}
              {floorPlanId && !loadingEntrances && entrances.length === 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No building entrances configured. An administrator needs to add entrances in the Indoor Mapping settings.
                </Alert>
              )}

              {/* Walking directions as alternative */}
              <Button
                variant="text"
                size="small"
                startIcon={<WalkIcon />}
                onClick={() => {
                  if (!destinationCoords) return;
                  const url = `https://www.google.com/maps/dir/?api=1&destination=${destinationCoords.lat},${destinationCoords.lng}&travelmode=walking`;
                  window.open(url, '_blank');
                }}
                disabled={!destinationCoords}
              >
                Walking Directions
              </Button>
            </Box>

            {/* Entrance Selection (if multiple entrances available) */}
            {entrances.length > 1 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Select Building Entrance:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {entrances.map((entrance) => (
                    <Chip
                      key={entrance.id}
                      icon={<EntranceIcon />}
                      label={entrance.name || `Entrance ${entrance.id}`}
                      onClick={() => setSelectedEntrance(entrance)}
                      color={selectedEntrance?.id === entrance.id ? 'primary' : 'default'}
                      variant={selectedEntrance?.id === entrance.id ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {navError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {navError}
              </Alert>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Full-screen Indoor Navigation */}
      {showIndoorNav && (
        <NavigationContainer onClose={() => setShowIndoorNav(false)} />
      )}
    </>
  );
};

/**
 * Main component wrapped with NavigationProvider
 */
const AlarmNavigationPanel = (props) => {
  return (
    <NavigationProvider>
      <AlarmNavigationPanelInner {...props} />
    </NavigationProvider>
  );
};

export default AlarmNavigationPanel;
